/** 1 = black, 2 = white. Used for stones/turns; 0 means empty where a board cell is involved. */
export type Color = 1 | 2;

export interface SgfSetupStone {
  /** 0 = empty (AE, i.e. clear a point), 1 = black, 2 = white */
  color: 0 | Color;
  x: number;
  y: number;
}

export interface SgfMove {
  color: Color;
  x: number;
  y: number;
  pass: boolean;
  /** Markup (CR/SQ/TR/MA/LB) attached to this move's node. */
  marks: Marker[];
}

export type MarkerType = 'circle' | 'square' | 'triangle' | 'mark' | 'label';

export interface Marker {
  type: MarkerType;
  x: number;
  y: number;
  /** Only present for `label` markers (SGF `LB`). */
  text?: string;
}

export interface SgfData {
  size: number | null;
  setup: SgfSetupStone[];
  moves: SgfMove[];
  initialTurn: Color | null;
  /** Markup (CR/SQ/TR/MA/LB) attached before the first move - shown on the empty/setup position. */
  rootMarks: Marker[];
}

interface Coord {
  x: number;
  y: number;
  pass: boolean;
}

function parseCoord(coordStr: string, boardSize: number | null): Coord | Coord[] {
  if (!coordStr || coordStr.length === 0) return { x: -1, y: -1, pass: true };

  if (coordStr.indexOf(':') !== -1) {
    const parts = coordStr.split(':');
    const p1 = parseCoord(parts[0] ?? '', boardSize);
    const p2 = parseCoord(parts[1] ?? '', boardSize);
    if (Array.isArray(p1) || Array.isArray(p2) || p1.pass || p2.pass) return [];
    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);
    const minY = Math.min(p1.y, p2.y);
    const maxY = Math.max(p1.y, p2.y);
    const coords: Coord[] = [];
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        coords.push({ x, y, pass: false });
      }
    }
    return coords;
  }

  const xChar = coordStr.charAt(0).toLowerCase();
  const yChar = coordStr.charAt(1).toLowerCase();
  const x = xChar.charCodeAt(0) - 97;
  const y = yChar.charCodeAt(0) - 97;

  if (boardSize && boardSize <= 19 && xChar === 't' && yChar === 't') {
    return { x, y, pass: true };
  }
  if (boardSize && (x < 0 || x >= boardSize || y < 0 || y >= boardSize)) {
    return { x, y, pass: true };
  }
  return { x, y, pass: false };
}

/**
 * A small, permissive SGF parser covering just what a game-record viewer
 * needs: board size, setup stones (AB/AW/AE), the move sequence (B/W,
 * including passes and compressed rectangle coordinates like "aa:bb"), the
 * player to move (PL), and markup (CR/SQ/TR/MA/LB). Only the first child of
 * a branch point is kept — later siblings (alternate variations) are
 * skipped. Markup is attached to whichever move most recently preceded it,
 * or to `rootMarks` if no move has occurred yet.
 */
export function parseSgf(sgf: string | null | undefined): SgfData {
  if (!sgf || typeof sgf !== 'string') {
    return { size: null, setup: [], moves: [], initialTurn: null, rootMarks: [] };
  }

  let size: number | null = null;
  const setup: SgfSetupStone[] = [];
  const moves: SgfMove[] = [];
  let initialTurn: Color | null = null;
  const rootMarks: Marker[] = [];

  let i = 0;
  let depth = 0;
  let skipUntilDepth = -1;
  const branchSeenAtDepth: Record<number, boolean> = {};

  while (i < sgf.length) {
    const ch = sgf[i];
    if (ch === '(') {
      depth++;
      if (skipUntilDepth !== -1) {
        i++;
        continue;
      }
      if (branchSeenAtDepth[depth]) {
        skipUntilDepth = depth;
      } else {
        branchSeenAtDepth[depth] = true;
      }
      i++;
    } else if (ch === ')') {
      if (skipUntilDepth === depth) {
        skipUntilDepth = -1;
      }
      delete branchSeenAtDepth[depth + 1];
      depth--;
      i++;
    } else if (ch === ';') {
      i++;
      if (skipUntilDepth !== -1) {
        continue;
      }

      const nodeMarks: Marker[] = [];

      while (i < sgf.length) {
        while (i < sgf.length && /\s/.test(sgf[i] as string)) i++;
        if (i >= sgf.length || sgf[i] === ';' || sgf[i] === '(' || sgf[i] === ')') break;

        let propIdent = '';
        while (i < sgf.length && /[A-Za-z]/.test(sgf[i] as string)) {
          propIdent += sgf[i];
          i++;
        }
        if (!propIdent) {
          i++;
          continue;
        }
        propIdent = propIdent.toUpperCase();

        const propValues: string[] = [];
        while (i < sgf.length) {
          while (i < sgf.length && /\s/.test(sgf[i] as string)) i++;
          if (i < sgf.length && sgf[i] === '[') {
            i++;
            let val = '';
            while (i < sgf.length) {
              if (sgf[i] === '\\' && i + 1 < sgf.length) {
                val += sgf[i + 1];
                i += 2;
              } else if (sgf[i] === ']') {
                i++;
                break;
              } else {
                val += sgf[i];
                i++;
              }
            }
            propValues.push(val.trim());
          } else {
            break;
          }
        }

        if (propIdent === 'SZ' && propValues.length > 0) {
          const szVal = parseInt(propValues[0] as string, 10);
          if (!isNaN(szVal) && szVal > 0) size = szVal;
        } else if (propIdent === 'PL' && propValues.length > 0) {
          const pl = (propValues[0] as string).toUpperCase();
          if (pl === 'B' || pl === '1') initialTurn = 1;
          else if (pl === 'W' || pl === '2') initialTurn = 2;
        } else if (propIdent === 'AB' || propIdent === 'AW' || propIdent === 'AE') {
          const color: 0 | Color = propIdent === 'AB' ? 1 : propIdent === 'AW' ? 2 : 0;
          for (const value of propValues) {
            const coords = parseCoord(value, size);
            if (Array.isArray(coords)) {
              for (const c of coords) {
                if (!c.pass) setup.push({ color, x: c.x, y: c.y });
              }
            } else if (!coords.pass) {
              setup.push({ color, x: coords.x, y: coords.y });
            }
          }
        } else if (propIdent === 'B' || propIdent === 'W') {
          const moveColor: Color = propIdent === 'B' ? 1 : 2;
          for (const value of propValues) {
            const coords = parseCoord(value, size);
            if (Array.isArray(coords)) {
              for (const c of coords) {
                moves.push({ color: moveColor, x: c.x, y: c.y, pass: c.pass, marks: [] });
              }
            } else {
              moves.push({ color: moveColor, x: coords.x, y: coords.y, pass: coords.pass, marks: [] });
            }
          }
        } else if (propIdent === 'CR' || propIdent === 'SQ' || propIdent === 'TR' || propIdent === 'MA') {
          const type: MarkerType = propIdent === 'CR' ? 'circle' : propIdent === 'SQ' ? 'square' : propIdent === 'TR' ? 'triangle' : 'mark';
          for (const value of propValues) {
            const coords = parseCoord(value, size);
            if (Array.isArray(coords)) {
              for (const c of coords) {
                if (!c.pass) nodeMarks.push({ type, x: c.x, y: c.y });
              }
            } else if (!coords.pass) {
              nodeMarks.push({ type, x: coords.x, y: coords.y });
            }
          }
        } else if (propIdent === 'LB') {
          for (const value of propValues) {
            const colonIndex = value.indexOf(':');
            if (colonIndex === -1) continue;
            const coords = parseCoord(value.slice(0, colonIndex), size);
            if (!Array.isArray(coords) && !coords.pass) {
              nodeMarks.push({ type: 'label', x: coords.x, y: coords.y, text: value.slice(colonIndex + 1) });
            }
          }
        }
      }

      if (nodeMarks.length > 0) {
        if (moves.length === 0) {
          rootMarks.push(...nodeMarks);
        } else {
          (moves[moves.length - 1] as SgfMove).marks.push(...nodeMarks);
        }
      }
    } else {
      i++;
    }
  }

  return { size, setup, moves, initialTurn, rootMarks };
}
